import { BoxV2 as Box, Button, Flex, TextInput } from 'portal-commons';
import { ComponentProps, FC, FunctionComponent, useEffect } from 'react';
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
} from 'react-hook-form';
import {
  faCircleXmark,
  IconDefinition,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  isValidEmail,
  isRequired,
  createMaxLengthValidator,
} from '../validators';
import { MAX_EMAIL_COUNT, MAX_EMAIL_LENGTH } from '../constants';
import Modal from './Modal';

export interface EmailEditPayload {
  emails: { email: string }[];
}

interface EmailEditModalProps {
  open: boolean;
  disabled: boolean;
  title: string;
  icon: IconDefinition;
  description?: string;
  emails: string[];
  onClose: () => void;
  onSubmit: (data: EmailEditPayload) => void;
}

const EmailEditModal: FunctionComponent<EmailEditModalProps> = ({
  open,
  disabled,
  title,
  icon,
  description,
  emails,
  onClose,
  onSubmit,
}) => {
  // Initialize form with dynamic default values
  const form = useForm<EmailEditPayload>({
    mode: 'onChange',
    defaultValues: {
      emails: emails.map((email) => ({ email })),
    },
  });

  const { isValid, isValidating } = form.formState;

  const emailArray = useFieldArray({
    control: form.control,
    name: 'emails',
  });

  // Reset form state only when modal opens
  useEffect(() => {
    if (open) {
      form.reset({
        emails: emails.map((email) => ({ email })),
      });
    }
  }, [open, emails, form]);

  return (
    <Modal
      open={open}
      showCloseButton={false}
      onClose={onClose}
      data-testid="emailEditModal"
    >
      <FormProvider {...form}>
        <form
          noValidate
          onSubmit={form.handleSubmit(onSubmit)}
          style={{ width: '100%' }}
          data-testid="emailEditForm"
        >
          <Flex sx={{ flexDirection: 'column', width: '100%' }}>
            {/* Modal Header: icon, title */}
            <Flex
              sx={{
                flexDirection: 'column',
                lineHeight: 'normal',
                alignItems: 'center',
              }}
            >
              <Flex
                sx={{
                  alignItems: 'center',
                  gap: 12,
                  py: '12px',
                  fontSize: '24px',
                  fontWeight: 400,
                  color: '#333E41',
                }}
              >
                <FontAwesomeIcon icon={icon} />
                <Box as="h2" sx={{ fontSize: '24px', fontWeight: '600' }}>
                  {title}
                </Box>
              </Flex>
              {description && (
                <Box
                  sx={{
                    fontSize: '14px',
                    color: '#4D5659',
                    width: '100%',
                    textAlign: 'left',
                    pb: '11px',
                    pt: '7px',
                  }}
                >
                  {description}
                </Box>
              )}
            </Flex>

            {/* Emails Form Block */}
            <Flex sx={{ flexDirection: 'column', mt: 8, gap: 16 }}>
              {emailArray.fields.map((field, index) => (
                <RemovableEmailField
                  key={field.id}
                  index={index}
                  removable={emailArray.fields.length > 1}
                  onRemove={() => emailArray.remove(index)}
                  disabled={disabled}
                />
              ))}

              {emailArray.fields.length < MAX_EMAIL_COUNT && (
                <AddEmailButton
                  disabled={disabled}
                  onClick={() => emailArray.append({ email: '' })}
                />
              )}
            </Flex>

            {/* Action Buttons */}
            <Flex
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                gap: 18,
                pt: '30px',
                pb: '40px',
              }}
            >
              <Button
                variant="outline"
                onClick={onClose}
                data-testid="cancelButton"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={disabled || isValidating || !isValid}
                data-testid="saveButton"
              >
                Save
              </Button>
            </Flex>
          </Flex>
        </form>
      </FormProvider>
    </Modal>
  );
};

interface RemovableEmailFieldProps {
  index: number;
  removable: boolean;
  onRemove: () => void;
  disabled: boolean;
}

const RemovableEmailField: FC<RemovableEmailFieldProps> = ({
  index,
  removable,
  onRemove,
  disabled,
}) => {
  const { control } = useFormContext();

  const emailRules = {
    validate: {
      isRequired,
      withValidMaxLength: createMaxLengthValidator(MAX_EMAIL_LENGTH),
      isEmail: isValidEmail,
    },
  };

  const showRemoveButton = removable && index !== 0;

  return (
    <Flex sx={{ alignItems: 'flex-start', width: '100%', gap: '10px' }}>
      {showRemoveButton && (
        <Box
          sx={{
            pt: '9px',
            color: '#666e71',
            fontSize: '18px',
            cursor: 'pointer',
            width: '19px',
            flexShrink: 0,
          }}
          onClick={onRemove}
          data-testid={`removeEmailButton_${index}`}
        >
          <FontAwesomeIcon icon={faCircleXmark} />
        </Box>
      )}
      <Box sx={{ flex: 1 }}>
        <Controller
          name={`emails.${index}.email`}
          control={control}
          rules={emailRules}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              {...field}
              required
              label="Email Address"
              maxLength={MAX_EMAIL_LENGTH}
              disabled={disabled}
              error={!!error}
              note={error?.message}
              data-testid={`emailInput_${index}`}
            />
          )}
        />
      </Box>
    </Flex>
  );
};

const AddEmailButton: FC<ComponentProps<typeof Box>> = ({
  sx,
  onClick,
  disabled,
  ...restProps
}) => {
  return (
    <Box
      data-testid="addEmailButton"
      role="button"
      tabIndex={0}
      sx={{
        mt: '-4px',
        alignItems: 'center',
        color: '#0091B3',
        cursor: 'pointer',
        fontWeight: 600,
        fontSize: '16px',
        width: 'fit-content',
        ...sx,
      }}
      onClick={(e: React.MouseEvent<HTMLDivElement>) =>
        !disabled && onClick?.(e)
      }
      {...restProps}
    >
      Add New Email Address
    </Box>
  );
};

export default EmailEditModal;
